export function formatCents(cents: number | null | undefined, currency = 'USD'): string {
  const v = (cents ?? 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(v);
  } catch {
    return `$${v.toFixed(2)}`;
  }
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export function formatPercent(v: number | null | undefined, digits = 2): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

export function formatNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

/**
 * Strip trailing `.0` / `.00` from a decimal string.
 *   "10.0"  → "10"
 *   "7.5"   → "7.5"
 *   "1.20"  → "1.2"
 *   "0.00"  → "0"
 */
function trimZeros(s: string): string {
  return s.includes('.') ? s.replace(/\.?0+$/, '') : s;
}

/**
 * Abbreviated dollar amount for chart axes / tight UI.
 *   $5,000   → $5k      |  $0       → $0
 *   $7,500   → $7.5k    |  $10,000  → $10k
 *   $1,250k  → $1.25M
 */
export function formatCentsCompact(cents: number | null | undefined): string {
  const v = Math.abs((cents ?? 0) / 100);
  const sign = (cents ?? 0) < 0 ? '−' : '';
  if (v === 0) return '$0';
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `${sign}$${trimZeros((m >= 10 ? m.toFixed(1) : m.toFixed(2)))}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return `${sign}$${trimZeros(k >= 100 ? k.toFixed(0) : k.toFixed(1))}k`;
  }
  // Sub-$1k: integer if integer, else two decimals.
  return Number.isInteger(v) ? `${sign}$${v}` : `${sign}$${v.toFixed(2)}`;
}

/** Abbreviated plain integer (e.g. for clicks / conversions axes). */
export function formatNumberCompact(n: number | null | undefined): string {
  const v = Math.abs(n ?? 0);
  const sign = (n ?? 0) < 0 ? '−' : '';
  if (v === 0) return '0';
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return `${sign}${trimZeros(m >= 10 ? m.toFixed(1) : m.toFixed(2))}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return `${sign}${trimZeros(k >= 100 ? k.toFixed(0) : k.toFixed(1))}k`;
  }
  return `${sign}${Math.round(v)}`;
}
